import React, { useState, useEffect } from 'react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { Key, Copy, Trash2, Plus, AlertTriangle, Check } from 'lucide-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './ApiKeyModal.css';

const ApiKeyModal = ({ isOpen, onClose, user, selectedWorkspace }) => {
  const { organization } = useOrganization();
  const clerk = useClerk();

  // State
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // New key generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Load keys on open
  useEffect(() => {
    if (isOpen && user && selectedWorkspace?.workspace_id) {
      loadApiKeys();
      // Reset state
      setShowNewKeyForm(false);
      setGeneratedKey(null);
      setNewKeyName('');
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, user, selectedWorkspace?.workspace_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadApiKeys = async () => {
    if (!selectedWorkspace?.workspace_id) return;

    setIsLoading(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/workspace-api-keys?workspace_id=${selectedWorkspace.workspace_id}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load API keys');
      }

      const data = await response.json();
      setApiKeys(data.keys || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/workspace-api-keys/generate?workspace_id=${selectedWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newKeyName.trim() }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate API key');
      }

      const data = await response.json();
      setGeneratedKey(data.api_key);
      setShowNewKeyForm(false);
      setNewKeyName('');

      // Reload the keys list
      await loadApiKeys();
    } catch (err) {
      setError(err.message || 'Failed to generate API key');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/workspace-api-keys/${keyId}?workspace_id=${selectedWorkspace.workspace_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }

      setSuccessMessage('API key revoked successfully');
      await loadApiKeys();
    } catch (err) {
      setError(err.message || 'Failed to revoke API key');
    }
  };

  const handleCopyKey = async () => {
    if (generatedKey) {
      try {
        await navigator.clipboard.writeText(generatedKey);
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      } catch (err) {
        setError('Failed to copy to clipboard');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="api-key-overlay" onClick={onClose}>
      <div className="api-key-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="api-key-header">
          <h2>
            <Key size={20} />
            API Keys
          </h2>
          <button className="api-key-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="api-key-body">
          {/* Generated Key Display */}
          {generatedKey && (
            <div className="api-key-generated">
              <div className="api-key-generated-header">
                <AlertTriangle size={20} className="warning-icon" />
                <span>Save this API key now. You won't be able to see it again!</span>
              </div>
              <div className="api-key-generated-value">
                <code>{generatedKey}</code>
                <button
                  className="api-key-copy-btn"
                  onClick={handleCopyKey}
                  title="Copy to clipboard"
                >
                  {copiedKey ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <button
                className="api-key-dismiss-btn"
                onClick={() => setGeneratedKey(null)}
              >
                I've saved my key
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="api-key-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="api-key-success">
              <Check size={16} />
              <span>{successMessage}</span>
            </div>
          )}

          {/* New Key Form */}
          {showNewKeyForm && !generatedKey && (
            <div className="api-key-form">
              <div className="api-key-form-group">
                <label>Key Name</label>
                <input
                  type="text"
                  className="api-key-input"
                  placeholder="e.g., Production Integration"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  autoFocus
                />
                <span className="api-key-hint">
                  Give your key a descriptive name to identify its purpose
                </span>
              </div>
              <div className="api-key-form-actions">
                <button
                  className="api-key-btn api-key-btn-cancel"
                  onClick={() => {
                    setShowNewKeyForm(false);
                    setNewKeyName('');
                    setError(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="api-key-btn api-key-btn-generate"
                  onClick={handleGenerateKey}
                  disabled={isGenerating || !newKeyName.trim()}
                >
                  {isGenerating ? (
                    <>
                      <span className="api-key-spinner" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key size={14} />
                      Generate Key
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Keys List */}
          {isLoading ? (
            <div className="api-key-loading">
              <div className="api-key-loading-spinner" />
              <span>Loading API keys...</span>
            </div>
          ) : (
            <>
              {!showNewKeyForm && !generatedKey && (
                <button
                  className="api-key-btn api-key-btn-new"
                  onClick={() => setShowNewKeyForm(true)}
                >
                  <Plus size={16} />
                  Generate New API Key
                </button>
              )}

              {apiKeys.length > 0 ? (
                <div className="api-key-list">
                  <h3>Existing Keys</h3>
                  {apiKeys.filter(key => key.is_active).map((key) => (
                    <div key={key.key_id} className="api-key-item">
                      <div className="api-key-item-info">
                        <div className="api-key-item-name">{key.name}</div>
                        <div className="api-key-item-meta">
                          <span className="api-key-prefix">{key.key_prefix}...</span>
                          <span className="api-key-dot">&#183;</span>
                          <span>Created {formatDate(key.created_at)}</span>
                          {key.last_used_at && (
                            <>
                              <span className="api-key-dot">&#183;</span>
                              <span>Last used {formatDate(key.last_used_at)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        className="api-key-revoke-btn"
                        onClick={() => handleRevokeKey(key.key_id)}
                        title="Revoke key"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                !showNewKeyForm && !generatedKey && (
                  <div className="api-key-empty">
                    <Key size={32} className="api-key-empty-icon" />
                    <p>No API keys yet</p>
                    <span>Generate an API key to allow external services to execute reports in this workspace.</span>
                  </div>
                )
              )}
            </>
          )}

          {/* Usage Instructions */}
          {!showNewKeyForm && !generatedKey && (
            <div className="api-key-usage">
              <h4>Usage</h4>
              <p>Use your API key to execute reports from external services:</p>
              <pre className="api-key-code">
{`curl -X POST "${getApiBaseUrl(getOrgSlug(organization))}/external/reports/run" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"report_id": "your_report_id"}'`}
              </pre>
            </div>
          )}
        </div>

        <div className="api-key-footer">
          <button className="api-key-btn api-key-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyModal;
